import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@emotion/react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Modal,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Snackbar,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import { approveRequest, getAppRequests } from '../../services/apis';
import { useApplicationContext } from '../../context/applicationContext';

const defaultStyles = {
  flexGrow: 1,
  p: 3,
  m: 3,
  mt: 8,
  ml: 0,
  mr: 0,
  backgroundColor: '#fff',
  overflow: 'auto',
  borderRadius: 2,
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50%',
  bgcolor: 'background.paper',
  border: '1px solid #ccc',
  borderRadius: '8px',
  boxShadow: 24,
  p: 4,
};

const headingStyles = { fontSize: '18px', fontWeight: 'bold' };

const AppRequests = () => {
  const theme = useTheme();

  const appRequestsData = useRef([]);

  const { appList } = useApplicationContext();

  const [appRequestedItems, setAppRequestedItems] = useState(['all']);
  const [selected, setSelected] = useState(appRequestedItems[0]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selecteGroupErr, setSelectedGroupErr] = useState(false);
  const [selectedGrpMsg, setSelectedGrpMsg] = useState('');
  const [disableBtn, setDisableBtn] = useState(false);
  const [snackState, setSnackState] = React.useState({
    open: false,
    vertical: 'top',
    horizontal: 'right',
  });
  const { vertical, horizontal, open } = snackState;

  const action = (
    <React.Fragment>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={() => setSnackState({ ...snackState, open: false })}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  const handleAppRequestsSelection = (item) => {
    setSelected(item);
    if (item === 'all') {
      setRows(appRequestsData.current);
    } else {
      const filteredRows = appRequestsData.current.filter(
        (appRequests) => appRequests.user_display_app_name === item
      );
      setRows(filteredRows);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleView = (row) => {
    setSelectedDetails(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedDetails(null);
    setSelectedGroup('');
  };

  const handleGroupChange = (event) => {
    setSelectedGroupErr(false);
    setSelectedGroup(event.target.value);
  };

  const handleGetAppRequests = async () => {
    if (loading) return;
    setError(null);
    setLoading(true);

    try {
      const {
        data: { user_requests },
      } = await getAppRequests();
      console.log(user_requests);
      appRequestsData.current = user_requests;

      if (user_requests && user_requests.length) {
        const columnsData = [...Object.keys(user_requests[0]), 'action']
          .filter((item) => item !== 'id' && item !== 'cognito_group_names')
          .map((key) => ({
            id: key,
            label: key.replaceAll('_', ' '),
            minWidth: 170,
            align: 'center',
            format: (value) => value.toLocaleString('en-US'),
          }));
        setColumns(columnsData);

        const rowsData = user_requests.map((requests) => ({
          ...requests,
        }));
        setRows(rowsData);
      } else {
        setColumns([]);
        setRows([]);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const grandRequest = async (approve) => {
    if (!selectedDetails) return;
    if (selectedGroup === '') {
      setSelectedGroupErr(true);
      return;
    }
    try {
      setDisableBtn(true);
      const payload = {
        id: selectedDetails.id,
        status: approve ? 'ACCEPTED' : 'CANCELLED',
        group_name: selectedGroup,
        user_name: selectedDetails.user_name,
        requested_app_name: selectedDetails.app_name,
      };

      const { data } = await approveRequest(payload);
      setSelectedGrpMsg(data);
      setSnackState({ ...snackState, open: true });
      await handleGetAppRequests();
    } catch (error) {
      setSelectedGrpMsg('Error approving request');
    } finally {
      setDisableBtn(false);
      handleCloseModal();
    }
  };

  useEffect(() => {
    handleGetAppRequests();
  }, []);

  useEffect(() => {
    const names = appList.map((app) => app.user_display_app_name) || [];
    setAppRequestedItems([...appRequestedItems, ...names]);
  }, [appList]);

  return (
    <Box>
      <Box component="main" sx={defaultStyles}>
        <Box display={'flex'} sx={{ maxHeight: '150px' }} gap={3}>
          <Typography variant="h6" sx={headingStyles}>
            Modules
          </Typography>
          <Box>
            {appRequestedItems.map((item, idx) => (
              <Button
                key={idx}
                variant={selected === item ? 'contained' : 'outlined'}
                color={selected === item ? 'active' : 'inherit'}
                onClick={() => handleAppRequestsSelection(item)}
                sx={{
                  borderColor: selected === item ? theme.palette.active.main : theme.border.color,
                  color:
                    selected === item ? theme.palette.primary.default : theme.palette.active.main,
                  marginRight: 2,
                  marginBottom: 1,
                }}
              >
                <Typography
                  sx={{
                    textTransform: 'capitalize',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    whiteSpace: 'normal', // Allow text to wrap to multiple lines
                    overflow: 'hidden', // Hide overflow text
                    textOverflow: 'ellipsis', // Show ellipsis when text overflows
                    lineHeight: '1.2', // Adjust line height
                    maxHeight: '2.6em', // Limit height to 2 lines (2 * line height of 1.8em = 3.6em)
                    maxWidth: '250px',
                    padding: '5px 10px',
                  }}
                >
                  {item.replace('_', ' ')}
                </Typography>
              </Button>
            ))}
          </Box>
        </Box>
      </Box>
      <Box component="main" sx={{ ...defaultStyles, mt: 0 }}>
        <Typography variant="h6" sx={{ ...headingStyles, mb: 2 }}>
          App Requests
        </Typography>
        {error && (
          <Box display="flex" justifyContent="center" alignItems="center" height={440}>
            <Typography variant="h6" color="error">
              {error}
            </Typography>
          </Box>
        )}
        <Modal open={openModal} onClose={handleCloseModal} aria-labelledby="modal-title">
          <Box sx={modalStyle} display={'flex'} flexDirection={'column'}>
            <Typography id="modal-title" variant="h6" component="h2" gutterBottom>
              Details
            </Typography>
            {selectedDetails && (
              <>
                <Grid container spacing={2} justifyContent="space-between">
                  <Grid item xs={6}>
                    <Typography variant="body1" sx={{ whiteSpace: 'normal' }}>
                      <strong>App Name:</strong> {selectedDetails.app_name}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'normal' }}>
                      <strong>Reason:</strong> {selectedDetails.reason}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1" sx={{ whiteSpace: 'normal' }}>
                      <strong>User Name:</strong> {selectedDetails.user_name}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'normal' }}>
                      <strong>Status:</strong> {selectedDetails.status}
                    </Typography>
                  </Grid>
                </Grid>

                <FormControl
                  variant="standard"
                  fullWidth
                  sx={{ mt: 3, width: '50%' }}
                  error={selecteGroupErr}
                >
                  <InputLabel id="cognito-group-select-label">Cognito Group Names</InputLabel>
                  <Select
                    labelId="cognito-group-select-label"
                    id="cognito-group-select"
                    value={selectedGroup}
                    onChange={handleGroupChange}
                  >
                    {Object.entries(selectedDetails?.cognito_group_names || {}).map(
                      ([key, value], index) => (
                        <MenuItem key={index} value={value}>
                          {key} - {value}
                        </MenuItem>
                      )
                    )}
                  </Select>
                  {selecteGroupErr && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                      Please select a group.
                    </Typography>
                  )}
                </FormControl>
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  grandRequest(true);
                }}
                disabled={disableBtn}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  grandRequest(false);
                }}
                disabled={disableBtn}
              >
                Cancel
              </Button>
              <Button variant="outlined" color="primary" onClick={handleCloseModal}>
                Close
              </Button>
            </Box>
          </Box>
        </Modal>

        <Snackbar
          anchorOrigin={{ vertical, horizontal }}
          open={open}
          onClose={() => setSnackState({ ...snackState, open: false })}
          message={selectedGrpMsg}
          key={vertical + horizontal}
          action={action}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={440}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        style={{ minWidth: column.minWidth }}
                        sx={{ fontWeight: 'bold', fontSize: '15px', textTransform: 'capitalize' }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, idx) => {
                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={idx}>
                          {columns.map((column) => {
                            const value = row[column.id];
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{
                                  fontSize: '15px',
                                  ...(column.id === 'reason' && {
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 200,
                                  }),
                                }}
                              >
                                {column.id === 'action' ? (
                                  <Button
                                    sx={{ backgroundColor: theme.palette.active.main }}
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => handleView(row)}
                                  >
                                    View
                                  </Button>
                                ) : column.format && typeof value === 'number' ? (
                                  column.format(value)
                                ) : (
                                  value
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default AppRequests;
